export const removeAllFacetsFromAppliedArray = (
  appliedFacet,
  listOfIgnoredFacets = ['storeId', 'sortByField', 'sortByDir']
) => {
  const currentAppliedFacets = appliedFacet;

  Object.keys(currentAppliedFacets).forEach((appliedFacetKey) => {
    if (!listOfIgnoredFacets.includes(appliedFacetKey)) {
      // Set every facet that's not storeId to an empty array
      currentAppliedFacets[appliedFacetKey] = [];
    }
  });

  return currentAppliedFacets;
};

const applied = (selection) => selection.applied || selection.isSelected;

const getOptionId = (option) => option.id ?? option.searchFacetOptionId;

const removeMultipleAppliedOptions = ({ multipleOptions, option }) => {
  const allIds = multipleOptions
    .map((eachOption) => getOptionId(eachOption))
    .join(',');

  return !allIds.includes(getOptionId(option));
};

const appliedOption = (selection) => (option) => {
  const { multipleOptions } = selection;
  if (multipleOptions) {
    return removeMultipleAppliedOptions({ multipleOptions, option });
  }
  const optionId = getOptionId(option);
  const selectionId = getOptionId(selection);

  return optionId != selectionId;
};

const clearSpecificFields = (newFacetSelection, currentFacets) => {
  const field = [
    'sortByDir',
    'sortByField',
    'storeId',
    ...(applied(newFacetSelection) ? ['price', 'reviewScore'] : []),
  ].find((check) => check == newFacetSelection.facetName);

  if (field) currentFacets.appliedFacets[field] = [];
};

const addInAppliedFacets = ({
  appliedFacets,
  multipleOptions,
  newFacetSelection,
}) => {
  if (multipleOptions) {
    appliedFacets.push(...multipleOptions);
    return;
  }
  appliedFacets.push(newFacetSelection);
};

const removeFromAppliedFacets = ({
  facetName,
  currentFacets,
  newFacetSelection,
}) => {
  currentFacets.appliedFacets[facetName] = currentFacets.appliedFacets[
    facetName
  ].filter(appliedOption(newFacetSelection));
};

const buildNewFacetSelection = (newFacetSelection, currentFacets) => {
  const { facetName, multipleOptions } = newFacetSelection;
  if (applied(newFacetSelection)) {
    addInAppliedFacets({
      appliedFacets: currentFacets.appliedFacets[facetName],
      multipleOptions,
      newFacetSelection,
    });
  } else if (currentFacets.appliedFacets) {
    removeFromAppliedFacets({
      facetName,
      currentFacets,
      newFacetSelection,
    });
  }
};

const checkCurrentFacetsObject = (currentFacets) =>
  !currentFacets || typeof currentFacets !== 'object';

// Clear all facets if there's any change in the department facet
const isDepartmentChanging = ({ facetName }, currentFacets) => {
  if (facetName === 'department') {
    currentFacets.appliedFacets = removeAllFacetsFromAppliedArray(
      currentFacets.appliedFacets
    );
  }
};

const initiateNewObjectToFacetIfNeeded = ({ facetName }, currentFacets) => {
  if (facetName && !currentFacets.appliedFacets[facetName]) {
    currentFacets.appliedFacets[facetName] = [];
  }
};

/*
 * Given a new facet selection (could be a selection or a deselection), we need to
 * recreate the list of applied facets that we have.
 */
const getUpdatedListOfAppliedFacets = (newFacetSelection, facetsFromState) => {
  let currentFacets = facetsFromState;

  if (checkCurrentFacetsObject(currentFacets)) return {};
  if (!newFacetSelection) return currentFacets;

  initiateNewObjectToFacetIfNeeded(newFacetSelection, currentFacets);
  isDepartmentChanging(newFacetSelection, currentFacets);
  clearSpecificFields(newFacetSelection, currentFacets);
  buildNewFacetSelection(newFacetSelection, currentFacets);

  return currentFacets;
};

export const updateQuickFacetSelections = (
  newFacetSelection,
  appliedQuickFacets
) => {
  const idsToUse = (newFacetSelection.ids || newFacetSelection.id).split(',');
  if (appliedQuickFacets.some((id) => idsToUse.includes(id))) {
    return appliedQuickFacets.filter((id) => !idsToUse.includes(id));
  }
  return appliedQuickFacets.concat(idsToUse);
};

export default getUpdatedListOfAppliedFacets;
